/**
 * FormulaGroupCardBody — collapsible body of a FormulaGroupCard.
 *
 * Uses the `grid-template-rows: 0fr / 1fr` animated trick so the open/close
 * transition is fluid and never flashes neighbouring layout.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';

import type {
  FormulaBlocksBody,
  FormulaGroup,
  FormulaSlidersBody,
} from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

import { ExpressionPreview } from './group';
import { SlidersBuilder } from './sliders';
import { SubformulasList } from './subformulas';

export interface FormulaGroupCardBodyProps {
  id: string;
  expanded: boolean;
  group: FormulaGroup;
  expression: string;
  onSlidersChange: (body: FormulaSlidersBody) => void;
  onBlocksChange: (body: FormulaBlocksBody) => void;
}

const FormulaGroupCardBody: FC<FormulaGroupCardBodyProps> = ({
  id,
  expanded,
  group,
  expression,
  onSlidersChange,
  onBlocksChange,
}) => (
    <div
      id={id}
      role="region"
      aria-label="Formula builder"
      aria-hidden={!expanded}
      data-state={expanded ? 'open' : 'closed'}
      className={cn(
        'grid transition-[grid-template-rows] duration-200 ease-out',
        expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
      )}
    >
      <div className="min-h-0 overflow-hidden">
        <div className="space-y-3 pt-2">
          {group.mode === 'sliders' ? (
            <>
              <SlidersBuilder body={group.sliders ?? { metrics: [] }} onChange={onSlidersChange} />
              <ExpressionPreview expression={expression} />
            </>
          ) : (
            <SubformulasList
              body={group.blocks}
              migrateFallbackName={group.name || 'Subformula 1'}
              onChange={onBlocksChange}
            />
          )}
        </div>
    </div>
  </div>
);

export default FormulaGroupCardBody;
